import { useMemo } from "react";
import { LoanConfigurationDto, LoanConfigWithGuaranteeIdsDto } from "api/borrower";
import { BorrowerContractFunnelV2Dto, ComparateurPricingStatus } from "api/pricing";
import { createContextSelectorHooks } from "core/utils/contextUtils";
import { MainBorrowerPricingContext, MainBorrowerPricingContextProps } from "../MainBorrowerPricingContext";
import { FILTER_TYPE_TO_FUNNEL_FIELD, MainBorrowerPricingState, SideMenuFilter, SidebarFilterType } from "../types";

// Type combiné pour les prêts enrichis avec les garantees sélectionnées
export interface EnrichedLoanConfiguration extends LoanConfigurationDto {
  selectedGuaranteeIds: string[]
  pendingConfig: LoanConfigWithGuaranteeIdsDto | undefined
}

export const {
  useSelector: useMainBorrowerPricingSelector,
  useSelectorWithEquality: useMainBorrowerPricingSelectorWithEquality
} = createContextSelectorHooks<MainBorrowerPricingContextProps, MainBorrowerPricingState>(
  MainBorrowerPricingContext,
  'MainBorrowerPricing'
)

export const useBorrowerShoppingCart = () => {
  const shoppingCart = useMainBorrowerPricingSelector(state => state.shoppingCart)
  const borrowerProjectId = useMainBorrowerPricingSelector(state => state.borrowerProjectId)
  return { shoppingCart, borrowerProjectId }
}

// Helper pour extraire les filtres actifs par type
export const getActiveFiltersByType = (filters: SideMenuFilter[]): Record<SidebarFilterType, string[]> => {
  const activeFilters: Record<SidebarFilterType, string[]> = {}

  filters.forEach(filter => {
    const activeItems = filter.items
      .filter(item => item.value)
      .map(item => item.id)

    if (activeItems.length > 0) {
      activeFilters[filter.type] = activeItems
    }
  })

  return activeFilters
}

// Helper pour filtrer les contract funnels de manière générique
export const filterContractFunnels = (
  contractFunnels: BorrowerContractFunnelV2Dto[],
  filters: SideMenuFilter[]
): BorrowerContractFunnelV2Dto[] => {
  if (!contractFunnels) return []

  const activeFiltersByType = getActiveFiltersByType(filters)

  // Si aucun filtre actif, retourner tous les funnels
  if (Object.keys(activeFiltersByType).length === 0) {
    return contractFunnels
  }

  return contractFunnels.filter(contractFunnel => {
    // Parcourir tous les types de filtres actifs
    for (const [filterType, activeIds] of Object.entries(activeFiltersByType)) {
      // Récupérer la fonction de mapping pour ce type de filtre
      const getFunnelField = FILTER_TYPE_TO_FUNNEL_FIELD[filterType as SidebarFilterType]

      // Si pas de mapping pour ce type de filtre, ignorer ce filtre (laisser passer)
      if (!getFunnelField) {
        continue
      }

      const funnelFieldValue = getFunnelField(contractFunnel)

      // Si le funnel n'a pas de valeur pour ce champ, ignorer ce filtre pour ce funnel
      if (!funnelFieldValue) {
        continue
      }

      // Si la valeur n'est pas dans les IDs actifs, filtrer ce funnel
      if (!activeIds.includes(funnelFieldValue)) {
        return false
      }
    }

    return true
  })
}

// Hook pour obtenir les funnels filtrés
export const useFilteredContractFunnels = () => {
  const contractFunnels = useMainBorrowerPricingSelector(state => state.contractFunnels)
  const filters = useMainBorrowerPricingSelector(state => state.filters)

  return useMemo(
    () => {
      const filtered = filterContractFunnels(contractFunnels, filters)
      return [...filtered].sort((a, b) => {
        const aHasError = a.pricingStatus === ComparateurPricingStatus.Error || a.pricingStatus === ComparateurPricingStatus.Timeout
        const bHasError = b.pricingStatus === ComparateurPricingStatus.Error || b.pricingStatus === ComparateurPricingStatus.Timeout
        if (aHasError === bHasError) return 0
        return aHasError ? 1 : -1
      })
    },
    [contractFunnels, filters]
  )
}

// Hook pour obtenir le nombre de filtres actifs
export const useActiveFiltersCount = () => {
  const filters = useMainBorrowerPricingSelector(state => state.filters)

  return useMemo(() => {
    return filters.reduce((count, filterGroup) => {
      return count + filterGroup.items.filter(item => item.value).length
    }, 0)
  }, [filters])
}

// ============================================
// Sélecteurs pour les prêts
// ============================================

// Helper pour combiner loansConfigurations et pendingLoanConfigs
export const enrichLoansWithPendingConfigs = (
  loansConfigurations: LoanConfigurationDto[],
  pendingLoanConfigs: LoanConfigWithGuaranteeIdsDto[]
): EnrichedLoanConfiguration[] => {
  return loansConfigurations.map(loanConfig => {
    const pendingConfig = pendingLoanConfigs.find(
      pending => pending.loanId === loanConfig.loan.id
    )
    return {
      ...loanConfig,
      selectedGuaranteeIds: pendingConfig?.guaranteeIds || [],
      pendingConfig
    }
  })
}

// Hook pour obtenir les prêts enrichis (combinaison loansConfigurations + pendingLoanConfigs)
export const useEnrichedLoans = (): EnrichedLoanConfiguration[] => {
  const loansConfigurations = useMainBorrowerPricingSelector(state => state.loansConfigurations)
  const pendingLoanConfigs = useMainBorrowerPricingSelector(state => state.pendingLoanConfigs)

  return useMemo(
    () => enrichLoansWithPendingConfigs(loansConfigurations, pendingLoanConfigs),
    [loansConfigurations, pendingLoanConfigs]
  )
}

// Hook pour obtenir les prêts actifs uniquement
export const useActiveLoans = (): EnrichedLoanConfiguration[] => {
  const enrichedLoans = useEnrichedLoans()

  return useMemo(
    () => enrichedLoans.filter(loan => loan.insuranceConfig.isActive),
    [enrichedLoans]
  )
}

// Hook pour obtenir les garantees sélectionnées pour un prêt spécifique
export const useLoanGuaranteeIds = (loanId: string): string[] => {
  const pendingLoanConfigs = useMainBorrowerPricingSelector(state => state.pendingLoanConfigs)

  return useMemo(() => {
    const pendingConfig = pendingLoanConfigs.find(config => config.loanId === loanId)
    return pendingConfig?.guaranteeIds || []
  }, [pendingLoanConfigs, loanId])
}

// ============================================
// Sélecteurs pour les loading states
// ============================================

// Hook pour vérifier si quelque chose est en cours de chargement
export const useIsAnyLoading = (): boolean => {
  const isLoading = useMainBorrowerPricingSelector(state => state.isLoading)
  const isPriceLoading = useMainBorrowerPricingSelector(state => state.isPriceLoading)
  const isContractFunnelsLoading = useMainBorrowerPricingSelector(state => state.isContractFunnelsLoading)

  return isLoading || isPriceLoading || isContractFunnelsLoading
}

// ============================================
// Sélecteurs pour les calculs de prix
// ============================================

// Hook pour calculer le prix total du panier
export const useShoppingCartTotal = () => {
  const shoppingCart = useMainBorrowerPricingSelector(state => state.shoppingCart)

  return useMemo(() => {
    const totalPrice = shoppingCart.reduce(
      (acc, item) => acc + (item.contractFunnel?.pricing?.clientPrice || 0),
      0
    )
    return {
      totalPrice,
      itemCount: shoppingCart.length
    }
  }, [shoppingCart])
}

// ============================================
// Sélecteurs pour les funnels
// ============================================

// Hook pour obtenir un funnel par son hash
export const useContractFunnelByHash = (hash: string | null): BorrowerContractFunnelV2Dto | undefined => {
  const contractFunnels = useMainBorrowerPricingSelector(state => state.contractFunnels)

  return useMemo(
    () => hash ? contractFunnels.find(funnel => funnel.hash === hash) : undefined,
    [contractFunnels, hash]
  )
}
